import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import DollarSignIcon from '@wix/da-ds/pkg/Icons/16x16/DollarSign';
import CloseIcon from '@wix/da-ds/pkg/Icons/16x16/Close';
import DollarPriceInput from '@wix/da-shared-react/pkg/DollarPriceInput';
import { QuestionFieldName } from '../../types';
import { MIN_PRICE } from '../../validation';
import getMaxPriceForAnswerChoice from '../../hooks/useMaxPriceForAnswerChoice';

import s from './AnswerChoiceField.scss';

interface Props {
  index: number;
  field: any;
  hasRemove: boolean;
  onRemove: (index: number) => void;
  inputPrefix?: React.ReactNode;
}

const AnswerChoiceField: React.FC<Props> = ({
  index,
  field,
  hasRemove,
  onRemove,
  inputPrefix,
}) => {
  const { t } = useTranslation();
  const maxPrice = useSelector(getMaxPriceForAnswerChoice);
  const { register, setValue, errors } = useFormContext();
  const [withPrice, setWithPrice] = useState(!!field.priceModifier);
  const setPrice = useCallback(
    newValue => {
      setValue(
        `${QuestionFieldName.ANSWER_CHOICES}[${index}].priceModifier`,
        newValue === '0' ? '1' : newValue
      );
    },
    [index, setValue]
  );

  const error = errors[QuestionFieldName.ANSWER_CHOICES]?.[index]?.answer;

  return (
    <div className={s['root']}>
      <div className={s['fields']}>
        <TextInput
          className={s['input']}
          wrapperClassName={s['input-wrapper']}
          id={`answer-text-${index}`}
          label={t('originalDeviationPopup.questionnaire.form.answer.label', {
            index: index + 1,
          })}
          ref={register()}
          name={`${QuestionFieldName.ANSWER_CHOICES}[${index}].answer`}
          defaultValue={field.answer}
          prefix={inputPrefix}
          maxLength={200}
          maxLengthHidden
          placeholder={t(
            'originalDeviationPopup.questionnaire.form.answer.label',
            {
              index: index + 1,
            }
          )}
          validationResult={error ? 'error' : undefined}
          details={error && error.message}
          isLabelHidden
          noBottomSpacing
        />
        {withPrice && (
          <div className={s['price']}>
            <DollarPriceInput
              label={t('originalDeviationPopup.questionnaire.form.price.label')}
              name={`${QuestionFieldName.ANSWER_CHOICES}[${index}].priceModifier`}
              ref={register()}
              defaultValue={field.priceModifier}
              min={MIN_PRICE}
              max={maxPrice}
              onChange={newPrice => setPrice(newPrice)}
              forceInteger
              isLabelHidden
              noBottomSpacing
            />
          </div>
        )}
      </div>
      {(!withPrice || hasRemove) && (
        <div className={s['actions']}>
          {!withPrice && (
            <IconButtonWithBackground
              className={s['action']}
              size="small"
              icon={DollarSignIcon}
              aria-label={t(
                'originalDeviationPopup.questionnaire.form.setPrice'
              )}
              onClick={() => setWithPrice(true)}
              tooltipText={t(
                'originalDeviationPopup.questionnaire.form.setPrice.tooltip'
              )}
            />
          )}
          {!withPrice && hasRemove && <span className={s['action-divider']} />}
          {hasRemove && (
            <IconButtonWithBackground
              className={s['action']}
              size="small"
              icon={CloseIcon}
              aria-label={t('originalDeviationPopup.questionnaire.form.remove')}
              onClick={() => onRemove(index)}
            />
          )}
        </div>
      )}
    </div>
  );
};
AnswerChoiceField.displayName = 'AnswerChoiceField';

export default AnswerChoiceField;
